// src/components/dashboard/AffiliateSection.tsx
// ============================================================================
// Satış Ortaklığı - Kullanıcı Sayfası (Minimal & Profesyonel)
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Users,
  Gift,
  Copy,
  CheckCircle,
  Calendar,
  Clock,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Sparkles,
  RefreshCw,
  AlertCircle,
  Loader2,
  ArrowRightCircle,
} from 'lucide-react';
import {
  getAffiliateStats,
  createReferralCode,
  AffiliateStats,
  CommissionPeriod,
  formatCurrency,
  formatDate,
  formatShortDate,
  getStatusColor,
  getStatusText,
} from '../../services/affiliate-api';
import toast from 'react-hot-toast';

// Status Icon Component
const StatusIcon: React.FC<{ status: CommissionPeriod['status'] }> = ({ status }) => {
  switch (status) {
    case 'Paid':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'PaymentPending':
      return <Clock className="w-4 h-4 text-amber-500" />;
    case 'AwaitingPayment':
      return <Calendar className="w-4 h-4 text-purple-500" />;
    case 'InProgress':
      return <TrendingUp className="w-4 h-4 text-blue-500" />;
    case 'CarriedOver':
      return <ArrowRightCircle className="w-4 h-4 text-sky-500" />;
    default:
      return <AlertCircle className="w-4 h-4 text-gray-500" />;
  }
};

export default function AffiliateSection() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCreatingCode, setIsCreatingCode] = useState(false);
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'periods' | 'users'>('periods');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAffiliateStats();
      setStats(data);
    } catch (err) {
      setError('Veriler yüklenirken bir hata oluştu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    try {
      setIsCreatingCode(true);
      const result = await createReferralCode();
      toast.success('Referans kodunuz oluşturuldu!');
      setStats(prev => prev ? {
        ...prev,
        referralCode: result.referralCode,
        referralCodeCreatedAt: result.createdAt
      } : null);
    } catch (err: any) {
      toast.error(err.message || 'Kod oluşturulamadı');
    } finally {
      setIsCreatingCode(false);
    }
  };

  const copyLink = () => {
    if (stats?.referralCode) {
      const link = `${window.location.origin}/register?ref=${stats.referralCode}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referans linki kopyalandı!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        <span className="ml-2 text-gray-500">Yükleniyor...</span>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-xl p-6 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  // Kod yoksa - Kod Oluşturma Ekranı
  if (!stats?.referralCode) {
    return <NoCodeView onCreateCode={handleCreateCode} isCreating={isCreatingCode} />;
  }

  // Kod varsa - Ana Panel
  // Müşteri başına tavan durumu: sınıra en yakın 3 müşteri (kazancı olanlar).
  // Tavan aylık DEĞİL, müşteri başına ömür boyu — değer backend'den (perCustomerCap).
  const perCustomerCap = stats.perCustomerCap ?? 0;
  const topCustomers = perCustomerCap > 0
    ? [...stats.referredUsers]
        .filter((u) => (u.earnedFromCustomer ?? 0) > 0)
        .sort((a, b) => (b.earnedFromCustomer ?? 0) - (a.earnedFromCustomer ?? 0))
        .slice(0, 3)
    : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-800">Satış Ortaklığı</h1>
        <p className="text-sm text-gray-500">Komisyonlarınızı takip edin</p>
      </div>

      {/* Referans Linki */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Referans Linkiniz</p>
              <p className="text-sm font-semibold text-gray-800 truncate">
                {`${window.location.origin}/register?ref=${stats.referralCode}`}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Kod: <span className="">{stats.referralCode}</span>
              </p>
            </div>
          </div>
          <button
            onClick={copyLink}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shrink-0 ${
              copied
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-50 hover:bg-orange-100 text-orange-600'
            }`}
          >
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Kopyalandı' : 'Linki Kopyala'}
          </button>
        </div>
      </div>

      {/* Müşteri başına 500 USD tavanı — toplam kazanca sınır yok */}
      {stats.perCustomerCap != null && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Yönlendirdiğiniz <span className="font-semibold">her müşteriden en fazla {formatCurrency(stats.perCustomerCap)}</span> kazanabilirsiniz;
            toplam kazancınıza sınır yoktur. Her müşterinin kalan hakkı aşağıdaki listede.
            {(stats.customersAtCap ?? 0) > 0 && (
              <>
                {' '}<span className="font-semibold">{stats.customersAtCap} müşteri</span> sınıra ulaştı — onlardan yeni kazanç eklenmez.
              </>
            )}
          </p>
        </div>
      )}

      {/* İstatistikler */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Kazanç</p>
          <p className="text-xl font-bold text-gray-800">{formatCurrency(stats.totalAllTimeEarnings)}</p>
          <p className="text-xs text-orange-500 mt-1">{stats.totalReferrals} kişi getirdin</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Ödenen</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(stats.totalPaidEarnings)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Bekleyen</p>
          <p className="text-xl font-bold text-amber-500">{formatCurrency(stats.totalPendingEarnings)}</p>
          {(stats.totalCarriedOverEarnings ?? 0) > 0 && (
            <p className="text-xs text-sky-600 mt-1">
              {formatCurrency(stats.totalCarriedOverEarnings!)} sonraki döneme devredildi
              {stats.minPayoutAmount != null && (
                <> (asgari {formatCurrency(stats.minPayoutAmount)} altı)</>
              )}
            </p>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Bu Ay</p>
          <p className="text-xl font-bold text-gray-700">{formatCurrency(stats.currentMonthEarnings)}</p>
        </div>
      </div>

      {/* Müşteri Başına Tavan — sınıra en yakın müşteriler */}
      {topCustomers.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-medium text-blue-800">Müşteri Başına Tavan</p>
            </div>
            <p className="text-sm font-semibold text-blue-800">
              {stats.customersAtCap ?? 0} / {stats.referredUsers.length} müşteri sınırda
            </p>
          </div>
          <div className="space-y-2">
            {topCustomers.map((u) => {
              const earned = u.earnedFromCustomer ?? 0;
              const pct = Math.min(100, (earned / perCustomerCap) * 100);
              return (
                <div key={u.id}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-blue-800">{u.maskedEmail}</span>
                    <span className={u.capReached ? 'font-semibold text-amber-700' : 'text-blue-700'}>
                      {formatCurrency(earned)} / {formatCurrency(perCustomerCap)}
                      {u.capReached ? ' · sınırda' : ''}
                    </span>
                  </div>
                  <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${u.capReached ? 'bg-amber-500' : 'bg-blue-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Her müşteriden en fazla {formatCurrency(perCustomerCap)}; toplam kazancınıza sınır yoktur.
            Tüm müşteriler için: Getirilen Kullanıcılar sekmesi.
          </p>
        </div>
      )}

      {/* Sonraki Ödeme Bildirimi */}
      {stats.nextPaymentDate && !!stats.nextPaymentAmount && (
        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex items-center gap-3">
          <Clock className="w-5 h-5 text-orange-500" />
          <div>
            <p className="text-sm font-medium text-orange-800">Sonraki Ödeme</p>
            <p className="text-sm text-orange-600">
              {formatDate(stats.nextPaymentDate)} • {formatCurrency(stats.nextPaymentAmount)}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('periods')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'periods'
                ? 'text-orange-600 bg-orange-50 border-b-2 border-orange-500'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Komisyon Dönemleri
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'users'
                ? 'text-orange-600 bg-orange-50 border-b-2 border-orange-500'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" />
            Getirilen Kullanıcılar ({stats.referredUsers.length})
          </button>
        </div>

        {/* Komisyon Dönemleri */}
        {activeTab === 'periods' && (
          <div className="divide-y divide-gray-100">
            {stats.periods.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Henüz komisyon dönemi yok</p>
                <p className="text-sm text-gray-400 mt-1">Kullanıcı getirdiğinizde burada görünecek</p>
              </div>
            ) : (
              stats.periods.map((period) => {
                const periodKey = `${period.year}-${period.month}`;
                const isExpanded = expandedPeriod === periodKey;
                const statusColor = getStatusColor(period.status);

                return (
                  <div key={periodKey}>
                    <div
                      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setExpandedPeriod(isExpanded ? null : periodKey)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                          <StatusIcon status={period.status} />
                          <div>
                            <p className="font-medium text-gray-800">{period.periodName}</p>
                            <p className="text-xs text-gray-500">{period.activeUsersCount} aktif kullanıcı</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-gray-800">{formatCurrency(period.commission)}</p>
                            {(period.carryInAmount ?? 0) > 0 && period.status !== 'Paid' && (
                              <p className="text-xs text-sky-600">
                                Devirle toplam: {formatCurrency(period.payableAmount ?? 0)}
                              </p>
                            )}
                          </div>
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${statusColor}15`,
                              color: statusColor,
                            }}
                          >
                            {getStatusText(period.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 bg-gray-50">
                        <div className="bg-white rounded-lg p-4 border border-gray-100">
                          {period.status === 'Paid' && period.paidAtUtc && (
                            <div className="flex items-center gap-2 text-sm text-green-700">
                              <CheckCircle className="w-4 h-4" />
                              <span>{formatDate(period.paidAtUtc)} tarihinde ödendi</span>
                              {period.transactionReference && (
                                <span className="text-gray-400">• Ref: {period.transactionReference}</span>
                              )}
                            </div>
                          )}
                          {/* Devir — kazanç kaybolmadı, sadece ertelendi */}
                          {period.status === 'CarriedOver' && period.carriedToPeriodName && (
                            <div className="flex items-start gap-2 text-sm text-sky-700">
                              <ArrowRightCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span>
                                {/* Devrin SEBEBİ — kullanıcı neden ödenmediğini bilmeli */}
                                {stats.minPayoutAmount != null ? (
                                  <>
                                    Bu dönemin komisyonu asgari ödeme tutarı olan{' '}
                                    <strong>{formatCurrency(stats.minPayoutAmount)}</strong>'ın altında kaldığı için{' '}
                                  </>
                                ) : (
                                  <>Bu dönemin komisyonu </>
                                )}
                                <strong>{period.carriedToPeriodName}</strong> dönemine devredildi.
                                Kazancınız kaybolmadı — o dönemin ödemesiyle birlikte yatırılacak.
                              </span>
                            </div>
                          )}
                          {(period.carryIns?.length ?? 0) > 0 && (
                            <div className="mt-2 p-2 bg-sky-50 border border-sky-200 rounded-lg">
                              <p className="text-sm font-medium text-sky-800 mb-1">
                                Bu döneme devirle gelen: {formatCurrency(period.carryInAmount ?? 0)}
                              </p>
                              {period.carryIns!.map((c) => (
                                <div key={`${c.year}-${c.month}`} className="flex justify-between text-xs text-sky-700">
                                  <span>{c.periodName} döneminden</span>
                                  <span className="font-medium">{formatCurrency(c.commission)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {period.status === 'PaymentPending' && (
                            <div className="flex items-center gap-2 text-sm text-amber-700">
                              <Clock className="w-4 h-4" />
                              <span>Ödeme penceresi: {period.paymentWindowText}</span>
                            </div>
                          )}
                          {period.status === 'AwaitingPayment' && (
                            <div className="flex items-center gap-2 text-sm text-purple-700">
                              <Calendar className="w-4 h-4" />
                              <span>Ödeme tarihi: {period.paymentWindowText}</span>
                            </div>
                          )}
                          {period.status === 'InProgress' && (
                            <div className="flex items-center gap-2 text-sm text-blue-700">
                              <TrendingUp className="w-4 h-4" />
                              <span>Dönem devam ediyor</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Getirilen Kullanıcılar */}
        {activeTab === 'users' && (
          <div className="divide-y divide-gray-100">
            {stats.referredUsers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Henüz getirilen kullanıcı yok</p>
                <p className="text-sm text-gray-400 mt-1">Kodunuzu paylaşarak başlayın</p>
              </div>
            ) : (
              stats.referredUsers.map((user, index) => (
                <div key={user.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{user.maskedEmail}</p>
                      <p className="text-xs text-gray-400">Kayıt: {formatShortDate(user.registeredAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Bu müşteriden kazanılan / kalan hak — tavan müşteri başına */}
                    {user.earnedFromCustomer != null && (
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-800">{formatCurrency(user.earnedFromCustomer)}</p>
                        <p className={`text-[11px] ${user.capReached ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                          {user.capReached ? 'Sınıra ulaştı' : `Kalan: ${formatCurrency(user.capRemaining ?? 0)}`}
                        </p>
                      </div>
                    )}
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        user.isActive
                          ? 'bg-green-50 text-green-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {user.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// KOD YOK - OLUŞTURMA EKRANI (Minimal)
// ============================================================================
interface NoCodeViewProps {
  onCreateCode: () => void;
  isCreating: boolean;
}

function NoCodeView({ onCreateCode, isCreating }: NoCodeViewProps) {
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Users className="w-7 h-7 text-orange-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Satış Ortaklığı Programı</h1>
        <p className="text-gray-500">Arkadaşlarını getir, birlikte kazan</p>
      </div>

      {/* Ana Kart */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800 mb-1">Nasıl Çalışır?</h2>
            <p className="text-sm text-gray-500">
              Referans linkinizi paylaşın, arkadaşlarınız kayıt olsun.
              Onların kazançlarından <span className="font-semibold text-orange-600">%20 komisyon</span> kazanın — her müşteriden 500 USD'ye kadar.
            </p>
          </div>
        </div>

        {/* Adımlar */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
              1
            </div>
            <p className="text-sm font-medium text-gray-700">Kod Oluştur</p>
            <p className="text-xs text-gray-400 mt-1">Tek tıkla</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
              2
            </div>
            <p className="text-sm font-medium text-gray-700">Paylaş</p>
            <p className="text-xs text-gray-400 mt-1">Arkadaşlarına gönder</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
              3
            </div>
            <p className="text-sm font-medium text-gray-700">Kazan</p>
            <p className="text-xs text-gray-400 mt-1">%20 komisyon al</p>
          </div>
        </div>

        {/* Oluştur Butonu */}
        <button
          onClick={onCreateCode}
          disabled={isCreating}
          className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isCreating ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Oluşturuluyor...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Referans Kodumu Oluştur
            </>
          )}
        </button>
      </div>

      {/* Müşteri başına kazanç sınırı bilgilendirmesi */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          Getirdiğiniz <span className="font-semibold">her müşteriden en fazla 500 USD</span> komisyon kazanabilirsiniz.
          Bir müşteriden kazancınız 500 USD'ye ulaştığında o müşteriden komisyon durur; diğer müşterileriniz etkilenmez.
          Toplam kazancınızda sınır yoktur — ne kadar çok müşteri getirirseniz o kadar kazanırsınız.
        </p>
      </div>

      {/* Bilgi Kutuları */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-orange-500">%20</p>
          <p className="text-xs text-gray-500 mt-1">Komisyon Oranı</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-gray-700">Aylık</p>
          <p className="text-xs text-gray-500 mt-1">Ödeme Sıklığı</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-gray-700">500 USD</p>
          <p className="text-xs text-gray-500 mt-1">Müşteri Başına Üst Sınır</p>
        </div>
      </div>
    </div>
  );
}